<script setup lang="ts">
/**
 * Orario «HH:mm» standard della piattaforma: l'UNICO controllo per i campi con la sola ora
 * (vietati `<input type="time">` e DatePicker `timeOnly` nel codice nuovo).
 *
 * Il campo di testo è sempre scrivibile a mano (HH:mm) e ha l'icona dell'orologio. Il pannello dipende dallo schermo:
 *  - desktop (≥ 992px): Popover con la griglia delle ore (00-23, 6 per riga) e dei minuti (a passi di `step`, 3 per riga),
 *    come il selettore del classico;
 *  - telefono (< 992px): pannello in basso (Drawer) con due ruote a scorrimento (scroll-snap) e la riga centrale
 *    evidenziata, più i chip degli `orariFrequenti` se passati.
 * Entrambi hanno «Adesso» (ora corrente, minuti arrotondati al passo), «Svuota» (solo se non `obbligatorio`) e «Chiudi»; Esc chiude.
 *
 * Contratto: v-model = stringa «HH:mm», `null` se vuoto (ora civile: nessun fuso, nessuna Date). Per i form che
 * salvano la stringa vuota: `valore-vuoto="''"`. Un valore già salvato fuori passo (es. 20:33) si vede nel campo così com'è,
 * nella griglia nessun minuto è evidenziato (nella ruota compare come voce in più) e NON viene arrotondato finché
 * l'utente non sceglie un minuto.
 * `inputId` e `name` vanno sul campo di testo; id/data-* sul contenitore (`$attrs`).
 */
import { computed, nextTick, ref, watch } from 'vue'
import { useMediaQuery } from '@vueuse/core'
import { useI18n } from 'vue-i18n'
import Popover from 'primevue/popover'
import Drawer from 'primevue/drawer'

const props = withDefaults(defineProps<{
  inputId?: string
  name?: string
  disabled?: boolean
  /** Se true non c'è «Svuota». */
  obbligatorio?: boolean
  /** Segnala il campo come non valido (bordo rosso, `aria-invalid`). */
  invalid?: boolean
  /** id dell'elemento che descrive il campo (messaggio d'errore), per `aria-describedby`. */
  describedby?: string
  /** Passo dei minuti. */
  step?: number
  /** Chip di orari frequenti (solo sul telefono): «07:00», «12:30»… */
  orariFrequenti?: readonly string[]
  /** Cosa emettere quando il campo è vuoto: `null` (default) oppure `''`. */
  valoreVuoto?: string | null
}>(), { inputId: undefined, name: undefined, disabled: false, obbligatorio: false, invalid: false, describedby: undefined, step: 5, orariFrequenti: undefined, valoreVuoto: null })

const modello = defineModel<string | null>({ default: null })
const { t } = useI18n()

/** Sotto il breakpoint del layout (992px, come AppSidebar) si usa il pannello da telefono. */
const mobile = useMediaQuery('(max-width: 991.98px)')

const due = (n: number) => String(n).padStart(2, '0')
const ORE = Array.from({ length: 24 }, (_, h) => due(h))
const MINUTI = computed(() => {
  const r: string[] = []
  for (let m = 0; m < 60; m += props.step) r.push(due(m))
  return r
})

const PARSE = /^(\d{1,2}):(\d{2})/
function parse(v: string | null | undefined): { h: string; m: string } | null {
  const x = PARSE.exec((v ?? '').trim())
  if (!x) return null
  const h = Number(x[1]); const m = Number(x[2])
  if (h > 23 || m > 59) return null
  return { h: due(h), m: due(m) }
}

const testo = ref<string>(modello.value ?? '')
watch(modello, (v) => { testo.value = v ?? '' })

const corrente = computed(() => parse(modello.value))
const nonValido = computed(() => testo.value.trim() !== '' && parse(testo.value) === null)
/** Minuti per la ruota: i passi + l'eventuale valore fuori passo già presente. */
const minutiRuota = computed(() => {
  const r = [...MINUTI.value]
  const m = corrente.value?.m
  if (m && !r.includes(m)) { r.push(m); r.sort() }
  return r
})

function emetti(v: string | null) {
  modello.value = v === null ? props.valoreVuoto : v
}

// ── Digitazione a mano ──────────────────────────────────────────────────────
function onInput(e: Event) {
  testo.value = (e.target as HTMLInputElement).value
  const p = parse(testo.value)
  if (p && /^\d{1,2}:\d{2}$/.test(testo.value.trim())) emetti(`${p.h}:${p.m}`)
}
function onBlur() {
  const tx = testo.value.trim()
  if (tx === '') { emetti(null); return }
  const p = parse(tx)
  if (p && /^\d{1,2}:\d{2}$/.test(tx)) { testo.value = `${p.h}:${p.m}`; emetti(testo.value) } else testo.value = modello.value ?? ''
}

// ── Pannello ────────────────────────────────────────────────────────────────
const pop = ref<InstanceType<typeof Popover> | null>(null)
const aperto = ref(false) // Drawer (telefono)

async function apri(e: Event) {
  if (props.disabled) return
  if (mobile.value) {
    aperto.value = true
    await nextTick(); await nextTick()
    posizionaRuote()
  } else {
    pop.value?.toggle(e)
  }
}
function chiudi() {
  aperto.value = false
  pop.value?.hide()
}

// Cambio di variante (rotazione del telefono, finestra ridimensionata): si chiude il pannello prima di sostituirlo
watch(mobile, () => { aperto.value = false; pop.value?.hide() }, { flush: 'pre' })

function scegliOra(h: string) {
  emetti(`${h}:${corrente.value?.m ?? '00'}`)
}
function scegliMinuto(m: string) {
  emetti(`${corrente.value?.h ?? '00'}:${m}`)
  chiudi()
}
function adesso() {
  const d = new Date()
  let h = d.getHours()
  let m = Math.round(d.getMinutes() / props.step) * props.step
  if (m >= 60) { m = 0; h = (h + 1) % 24 }
  emetti(`${due(h)}:${due(m)}`)
  chiudi()
}
function svuota() {
  emetti(null)
  chiudi()
}

/** Frecce: da un pulsante all'altro della stessa griglia (Su/Giù = una riga). */
function onGrigliaKey(e: KeyboardEvent, colonne: number) {
  const delta = ({ ArrowRight: 1, ArrowLeft: -1, ArrowDown: colonne, ArrowUp: -colonne } as Record<string, number>)[e.key]
  if (delta === undefined) return
  const bottoni = Array.from((e.currentTarget as HTMLElement).querySelectorAll<HTMLButtonElement>('button'))
  const i = bottoni.indexOf(document.activeElement as HTMLButtonElement)
  const prossimo = bottoni[i + delta]
  if (i >= 0 && prossimo) { e.preventDefault(); prossimo.focus() }
}

// ── Ruote (telefono) ────────────────────────────────────────────────────────
const RIGA = 40
const colOre = ref<HTMLElement | null>(null)
const colMin = ref<HTMLElement | null>(null)
let programmatico = false

function posizionaRuote() {
  programmatico = true
  const p = corrente.value
  colOre.value?.scrollTo({ top: (p ? ORE.indexOf(p.h) : 12) * RIGA })
  colMin.value?.scrollTo({ top: (p ? Math.max(0, minutiRuota.value.indexOf(p.m)) : 0) * RIGA })
  setTimeout(() => { programmatico = false }, 250)
}

let timer: ReturnType<typeof setTimeout> | undefined
function onScrollRuota(col: 'h' | 'm') {
  if (programmatico) return
  clearTimeout(timer)
  timer = setTimeout(() => {
    const el = col === 'h' ? colOre.value : colMin.value
    if (!el) return
    const i = Math.round(el.scrollTop / RIGA)
    const p = corrente.value
    if (col === 'h') {
      const h = ORE[Math.min(i, ORE.length - 1)]!
      if (h !== p?.h) emetti(`${h}:${p?.m ?? '00'}`)
    } else {
      const m = minutiRuota.value[Math.min(i, minutiRuota.value.length - 1)]!
      if (m !== p?.m) emetti(`${p?.h ?? '00'}:${m}`)
    }
  }, 120)
}
function vaiARuota(col: 'h' | 'm', i: number) {
  (col === 'h' ? colOre : colMin).value?.scrollTo({ top: i * RIGA, behavior: 'smooth' })
}
function scegliChip(c: string) {
  emetti(c)
  posizionaRuote()
}
</script>

<template>
  <div class="ora-input">
    <input
      :id="inputId"
      :name="name"
      :value="testo"
      type="text"
      inputmode="numeric"
      maxlength="5"
      autocomplete="off"
      placeholder="HH:mm"
      class="p-inputtext ora-campo"
      :class="{ 'p-invalid': nonValido || invalid }"
      :aria-invalid="nonValido || invalid"
      :aria-describedby="describedby"
      :disabled="disabled"
      :aria-label="t('common.ora.campo', 'Orario (HH:mm)')"
      @input="onInput"
      @blur="onBlur"
    >
    <button
      type="button"
      class="ora-icona"
      :disabled="disabled"
      :aria-label="t('common.ora.apri', 'Scegli l\'orario')"
      @click="apri"
    >
      <i class="pi pi-clock" />
    </button>

    <!-- Desktop: griglia -->
    <Popover ref="pop">
      <div class="ora-pannello">
        <div class="ora-colonne">
          <div class="ora-colonna">
            <div class="ora-titolo">
              {{ t('common.ora.ore', 'Ore') }}
            </div>
            <div
              class="ora-griglia ora-ore"
              role="group"
              :aria-label="t('common.ora.ore', 'Ore')"
              @keydown="onGrigliaKey($event, 6)"
            >
              <button
                v-for="h in ORE"
                :key="h"
                type="button"
                class="ora-cella"
                :class="{ attiva: corrente?.h === h }"
                :aria-pressed="corrente?.h === h"
                :aria-label="`${t('common.ora.ore', 'Ore')} ${h}`"
                @click="scegliOra(h)"
              >
                {{ h }}
              </button>
            </div>
          </div>
          <div class="ora-colonna">
            <div class="ora-titolo">
              {{ t('common.ora.minuti', 'Minuti') }}
            </div>
            <div
              class="ora-griglia ora-minuti"
              role="group"
              :aria-label="t('common.ora.minuti', 'Minuti')"
              @keydown="onGrigliaKey($event, 3)"
            >
              <button
                v-for="m in MINUTI"
                :key="m"
                type="button"
                class="ora-cella"
                :class="{ attiva: corrente?.m === m }"
                :aria-pressed="corrente?.m === m"
                :aria-label="`${t('common.ora.minuti', 'Minuti')} ${m}`"
                @click="scegliMinuto(m)"
              >
                {{ m }}
              </button>
            </div>
          </div>
        </div>
        <div class="ora-azioni">
          <button
            type="button"
            class="ora-azione"
            @click="adesso"
          >
            {{ t('common.ora.adesso', 'Adesso') }}
          </button>
          <button
            v-if="!obbligatorio"
            type="button"
            class="ora-azione"
            @click="svuota"
          >
            {{ t('common.ora.svuota', 'Svuota') }}
          </button>
          <button
            type="button"
            class="ora-azione ora-chiudi"
            @click="chiudi"
          >
            {{ t('common.ora.chiudi', 'Chiudi') }}
          </button>
        </div>
      </div>
    </Popover>

    <!-- Telefono: pannello in basso con ruote a scorrimento -->
    <Drawer
      v-model:visible="aperto"
      position="bottom"
      :base-z-index="10000"
      :header="t('common.ora.titolo', 'Scegli l\'orario')"
      class="ora-drawer"
    >
      <div class="ora-pannello ora-mobile">
        <div
          v-if="orariFrequenti?.length"
          class="ora-chip"
          role="group"
          :aria-label="t('common.ora.frequenti', 'Orari frequenti')"
        >
          <button
            v-for="c in orariFrequenti"
            :key="c"
            type="button"
            :class="{ attiva: modello === c }"
            :aria-pressed="modello === c"
            @click="scegliChip(c)"
          >
            {{ c }}
          </button>
        </div>
        <div class="ora-ruote">
          <div
            class="ora-banda"
            aria-hidden="true"
          />
          <div
            ref="colOre"
            class="ora-ruota"
            role="listbox"
            :aria-label="t('common.ora.ore', 'Ore')"
            @scroll="onScrollRuota('h')"
          >
            <div class="ora-pad" />
            <div
              v-for="(h, i) in ORE"
              :key="h"
              class="ora-voce"
              :class="{ attiva: corrente?.h === h }"
              role="option"
              :aria-selected="corrente?.h === h"
              @click="vaiARuota('h', i)"
            >
              {{ h }}
            </div>
            <div class="ora-pad" />
          </div>
          <div
            class="ora-due"
            aria-hidden="true"
          >
            :
          </div>
          <div
            ref="colMin"
            class="ora-ruota"
            role="listbox"
            :aria-label="t('common.ora.minuti', 'Minuti')"
            @scroll="onScrollRuota('m')"
          >
            <div class="ora-pad" />
            <div
              v-for="(m, i) in minutiRuota"
              :key="m"
              class="ora-voce"
              :class="{ attiva: corrente?.m === m }"
              role="option"
              :aria-selected="corrente?.m === m"
              @click="vaiARuota('m', i)"
            >
              {{ m }}
            </div>
            <div class="ora-pad" />
          </div>
        </div>
        <div class="ora-azioni">
          <button
            type="button"
            class="ora-azione"
            @click="adesso"
          >
            {{ t('common.ora.adesso', 'Adesso') }}
          </button>
          <button
            v-if="!obbligatorio"
            type="button"
            class="ora-azione"
            @click="svuota"
          >
            {{ t('common.ora.svuota', 'Svuota') }}
          </button>
          <button
            type="button"
            class="ora-azione ora-chiudi"
            @click="chiudi"
          >
            {{ t('common.ora.chiudi', 'Chiudi') }}
          </button>
        </div>
      </div>
    </Drawer>
  </div>
</template>

<style scoped>
.ora-input {
  display: inline-flex;
  align-items: center;
  gap: 0.15rem;
}
.ora-campo {
  width: 5.5rem;
  text-align: center;
}
.ora-icona {
  border: 0;
  background: none;
  cursor: pointer;
  color: var(--p-text-muted-color);
  padding: 0.4rem;
  border-radius: var(--p-border-radius-md);
}
.ora-icona:hover:not(:disabled) {
  color: var(--p-primary-color);
}
.ora-icona:focus-visible,
.ora-cella:focus-visible,
.ora-azione:focus-visible,
.ora-chip button:focus-visible {
  outline: 2px solid var(--p-primary-color);
  outline-offset: 1px;
}
.ora-pannello {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  max-width: calc(100vw - 3rem);
}
.ora-colonne {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}
.ora-titolo {
  font-size: 0.78rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: var(--p-text-muted-color);
  margin-bottom: 0.35rem;
}
.ora-griglia {
  display: grid;
  gap: 0.25rem;
}
.ora-ore { grid-template-columns: repeat(6, 2.4rem); }
.ora-minuti { grid-template-columns: repeat(3, 2.4rem); }
.ora-cella {
  height: 2.1rem;
  border: 1px solid var(--p-content-border-color, var(--p-surface-200));
  background: var(--p-content-background, transparent);
  color: var(--p-text-color);
  border-radius: var(--p-border-radius-md);
  cursor: pointer;
  font: inherit;
  font-size: 0.85rem;
}
.ora-cella:hover {
  background: var(--p-content-hover-background, var(--p-surface-100));
}
.ora-cella.attiva {
  background: var(--p-primary-color);
  border-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color, #fff);
  font-weight: 600;
}
.ora-azioni {
  display: flex;
  gap: 0.5rem;
  border-top: 1px solid var(--p-content-border-color, var(--p-surface-200));
  padding-top: 0.6rem;
}
.ora-azione {
  border: 0;
  background: none;
  color: var(--p-primary-color);
  cursor: pointer;
  font: inherit;
  font-size: 0.95rem;
  padding: 0.4rem 0.6rem;
  border-radius: var(--p-border-radius-md);
}
.ora-azione:hover {
  background: var(--p-content-hover-background, var(--p-surface-100));
}
.ora-chiudi {
  margin-left: auto;
  color: var(--p-text-muted-color);
}

/* Telefono: ruote */
.ora-mobile {
  max-width: none;
  align-items: stretch;
}
.ora-chip {
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  justify-content: center;
}
.ora-chip button {
  border: 1px solid var(--p-content-border-color, var(--p-surface-200));
  background: transparent;
  color: var(--p-text-color);
  border-radius: 999px;
  padding: 0.35rem 0.8rem;
  font: inherit;
  font-size: 0.9rem;
  cursor: pointer;
}
.ora-chip button.attiva {
  background: var(--p-primary-color);
  border-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color, #fff);
}
.ora-ruote {
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}
.ora-banda {
  position: absolute;
  left: 15%;
  right: 15%;
  top: 50%;
  height: 40px;
  margin-top: -20px;
  background: color-mix(in srgb, var(--p-primary-color) 14%, transparent);
  border-radius: var(--p-border-radius-md);
  pointer-events: none;
}
.ora-ruota {
  height: 200px;
  width: 6rem;
  overflow-y: auto;
  scroll-snap-type: y mandatory;
  scrollbar-width: none;
  text-align: center;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}
.ora-ruota::-webkit-scrollbar { display: none; }
.ora-pad { height: 80px; }
.ora-voce {
  height: 40px;
  line-height: 40px;
  scroll-snap-align: center;
  cursor: pointer;
  color: var(--p-text-muted-color);
  font-size: 1.2rem;
}
.ora-voce.attiva {
  color: var(--p-text-color);
  font-weight: 700;
}
.ora-due {
  font-weight: 700;
  font-size: 1.2rem;
}
@media (prefers-reduced-motion: reduce) {
  .ora-ruota { scroll-behavior: auto; }
}
</style>

<!-- Il Drawer è in un teleport (fuori dal componente): stile non scoped. Il valore di default di PrimeVue è 10rem. -->
<style>
.p-drawer.ora-drawer {
  height: auto !important;
  max-height: 90vh;
  border-top-left-radius: 1rem;
  border-top-right-radius: 1rem;
}
</style>
